/**
 * Normalize recipe name for URL: lowercase and hyphen-connected.
 * Handles Unicode characters by converting them to ASCII equivalents.
 * @param {string} name - Recipe name to normalize
 * @returns {string} Normalized name suitable for URL
 */
export const normalizeRecipeName = (name) => {
  if (!name) return "";
  
  // Character transliteration map for common special characters
  const TRANSLIT_MAP = {
    // Scandinavian
    'ø': 'o', 'Ø': 'o',
    'å': 'a', 'Å': 'a',
    'æ': 'ae', 'Æ': 'ae',
    // German
    'ä': 'ae', 'Ä': 'ae',
    'ö': 'oe', 'Ö': 'oe',
    'ü': 'ue', 'Ü': 'ue',
    'ß': 'ss',
    // French/other
    'é': 'e', 'è': 'e', 'ê': 'e', 'ë': 'e',
    'É': 'e', 'È': 'e', 'Ê': 'e', 'Ë': 'e',
    'à': 'a', 'á': 'a', 'â': 'a', 'ã': 'a',
    'À': 'a', 'Á': 'a', 'Â': 'a', 'Ã': 'a',
    'í': 'i', 'ì': 'i', 'î': 'i', 'ï': 'i',
    'Í': 'i', 'Ì': 'i', 'Î': 'i', 'Ï': 'i',
    'ó': 'o', 'ò': 'o', 'ô': 'o', 'õ': 'o',
    'Ó': 'o', 'Ò': 'o', 'Ô': 'o', 'Õ': 'o',
    'ú': 'u', 'ù': 'u', 'û': 'u',
    'Ú': 'u', 'Ù': 'u', 'Û': 'u',
    'ç': 'c', 'Ç': 'c',
    'ñ': 'n', 'Ñ': 'n',
  };
  
  // Convert to lowercase and apply transliteration
  let normalized = name.toLowerCase();
  normalized = normalized.split('').map(char => TRANSLIT_MAP[char] || char).join('');
  
  // Normalize Unicode characters (NFD = decompose remaining diacritics)
  normalized = normalized.normalize('NFD');
  // Remove any remaining diacritical marks (combining characters)
  normalized = normalized.replace(/[\u0300-\u036f]/g, '');
  
  // Replace spaces and special characters with hyphens
  normalized = normalized.replace(/[^\w\s-]/g, ""); // Remove special chars except hyphens
  normalized = normalized.replace(/[\s_]+/g, "-"); // Replace spaces/underscores with hyphens
  normalized = normalized.replace(/-+/g, "-"); // Replace multiple hyphens with single
  normalized = normalized.replace(/^-+|-+$/g, ""); // Remove leading/trailing hyphens
  return normalized;
};

/**
 * Get the creator username from a recipe object.
 * @param {Object} recipe - Recipe object with creator information
 * @returns {string} Creator username or email (lowercased)
 */
export const getCreatorUsername = (recipe) => {
  if (!recipe || !recipe.creator) return "";
  // Prefer username, fallback to email
  const username = recipe.creator.username || recipe.creator.email || "";
  return username.toLowerCase();
};

/**
 * Build a recipe URL using creator/name format.
 * @param {Object} recipe - Recipe object with name and creator
 * @returns {string} Recipe URL in format /recipes/{creator}/{name}
 */
export const getRecipeUrl = (recipe) => {
  if (!recipe) return "";
  const creator = getCreatorUsername(recipe);
  const normalizedName = normalizeRecipeName(recipe.name);
  if (!creator || !normalizedName) {
    // Fallback to UUID if we can't build creator/name URL
    return recipe.recipe_uuid ? `/recipes/${recipe.recipe_uuid}` : "/recipes";
  }
  return `/recipes/${creator}/${normalizedName}`;
};
